import { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    IconButton,
    Snackbar,
    Stack,
    useTheme,
} from '@mui/material';

import { Icon } from '../../shared/Icon';
import {
    Listbox,
    ListboxItem,
    ListboxSection,
} from '../../shared/Listbox/Listbox';
import { CategoryModal } from '../Category/CategoryModal';
import { CategoryTranslationStatusBadge } from '../Category/CategoryTranslationStatusBadge';

import {
    PortalCategoryDTO,
    usePortalFeed,
    usePortalLocalization,
} from '../../hooks/portal';

import { assert } from '../../utils/assert';
import { errorLikeToMessage } from '../../utils/error';

import styles from './CategoryListBox.module.scss';

export interface CategoryListBoxProps {
    categories: Array<PortalCategoryDTO>;
    selectedCategory?: string;
    onCategorySelected: (categoryId: string) => void;
    onCategorySaved: (category: PortalCategoryDTO) => Promise<void>;
    editable?: boolean;
}

export const CategoryListBox = ({
    categories: rawCategories,
    selectedCategory,
    onCategorySelected,
    onCategorySaved,
    editable,
}: CategoryListBoxProps) => {
    const [swapPositionError, setSwapPositionError] = useState<string>();

    const categories = useMemo(() => {
        return rawCategories.toSorted(
            (a, b) =>
                (a.position ?? Number.MAX_SAFE_INTEGER) -
                (b.position ?? Number.MAX_SAFE_INTEGER),
        );
    }, [rawCategories]);

    const { data: allLocalizations = [] } = usePortalLocalization();
    const translationsByLocalizationId = useMemo(() => {
        const index = new Map<string, Record<string, string>>();
        for (const loc of allLocalizations) {
            let entry = index.get(loc.id);
            if (!entry) {
                entry = {};
                index.set(loc.id, entry);
            }
            entry[loc.language] = loc.translation;
        }
        return index;
    }, [allLocalizations]);

    const handleOnChangePosition = useCallback(
        async (category: PortalCategoryDTO, direction: number) => {
            const index = categories.indexOf(category);
            assert(
                index >= 0,
                "Couldn't find the category to change position on",
            );

            if (
                (direction < 0 && index === 0) ||
                (direction > 0 && index + 1 === categories.length)
            ) {
                return;
            }

            const adjacentCategory = categories[index + Math.sign(direction)];

            const oldPosition = category.position;
            let newPosition = adjacentCategory.position;
            assert(
                oldPosition !== undefined,
                'Position of category is undefined, this should never happen. Check state in database',
            );
            assert(
                newPosition !== undefined,
                'Position of category is undefined, this should never happen. Check state in database',
            );

            if (newPosition === category.position) {
                // While this should rarely (never?) happen, we deal with the scenario by moving one extra step
                newPosition += Math.sign(direction);
            }

            Promise.all([
                onCategorySaved({
                    ...category,
                    position: newPosition,
                }),
                onCategorySaved({
                    ...adjacentCategory,
                    position: oldPosition,
                }),
            ]).catch((e) => {
                setSwapPositionError(errorLikeToMessage(e));
            });
        },
        [categories],
    );

    return (
        <>
            <Snackbar
                open={swapPositionError !== undefined}
                autoHideDuration={6000}
                onClose={() => setSwapPositionError(undefined)}
            >
                <Alert severity="error" title="Failed to change position">
                    {swapPositionError}
                </Alert>
            </Snackbar>
            <Listbox
                direction="horizontal"
                selectedValues={
                    selectedCategory ? [selectedCategory] : undefined
                }
                onSelected={(key) => {
                    onCategorySelected(key);
                }}
                className={styles.categorySelector}
            >
                <ListboxSection>
                    <ListboxItem
                        value={'featured'}
                        className={styles.categoryItem}
                    >
                        <Avatar
                            sx={{
                                width: 56,
                                height: 56,
                            }}
                        >
                            <Icon name="star" />
                        </Avatar>
                        <p>Featured</p>
                    </ListboxItem>
                    {categories?.map((category) => {
                        return (
                            <CategoryListItem
                                editable={editable}
                                key={category.id}
                                category={category}
                                translations={
                                    category.portalLocalizationId
                                        ? translationsByLocalizationId.get(
                                              category.portalLocalizationId,
                                          )
                                        : undefined
                                }
                                onSave={(category) => onCategorySaved(category)}
                                onChangePosition={(direction) =>
                                    handleOnChangePosition(category, direction)
                                }
                            />
                        );
                    })}
                </ListboxSection>
            </Listbox>
        </>
    );
};

interface CategoryListItemProps {
    category: PortalCategoryDTO;
    translations: Record<string, string> | undefined;
    onSave?: (category: PortalCategoryDTO) => void;
    onChangePosition?: (direction: number) => void;
    editable?: boolean;
}
const CategoryListItem = ({
    category: c,
    translations,
    onSave,
    onChangePosition,
    editable,
}: CategoryListItemProps) => {
    const { data: feed } = usePortalFeed();
    const [isEditing, setIsEditing] = useState(false);
    const theme = useTheme();

    const isActive = feed?.categories?.some(
        (feedCategory) => feedCategory.categoryId === c.id,
    );

    return (
        <Stack sx={{ alignItems: 'center' }}>
            <ListboxItem
                key={c.id}
                value={c.id}
                className={styles.categoryItem}
            >
                <CategoryModal
                    open={isEditing}
                    onClose={() => setIsEditing(false)}
                    onSave={(category) => onSave?.(category)}
                    initialValue={c}
                />
                <CategoryTranslationStatusBadge
                    category={c}
                    translations={translations}
                >
                    <Avatar
                        src={c.image?.replace('{0}', '@3x')}
                        alt={c.name}
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: isActive
                                ? theme.palette.primary.main
                                : undefined,
                        }}
                    >
                        {c.name.slice(0, 2)}
                    </Avatar>
                </CategoryTranslationStatusBadge>
                <p>{c.name}</p>
            </ListboxItem>
            {editable && (
                <Stack direction="row">
                    <IconButton onClick={() => onChangePosition?.(-1)}>
                        <Icon name="arrow_back" />
                    </IconButton>
                    <IconButton onClick={() => setIsEditing(true)}>
                        <Icon name="edit" />
                    </IconButton>
                    <IconButton onClick={() => onChangePosition?.(1)}>
                        <Icon name="arrow_forward" />
                    </IconButton>
                </Stack>
            )}
        </Stack>
    );
};
